import Head from 'next/head'
import Script from 'next/script'
import { useEffect, useMemo } from 'react'
import facebookActions from 'src/components/facebookPixel/facebookActions'
import * as gtg from 'src/services/gtag'
import * as gtm from 'src/services/gtm'
import { ProductResponse, Variation } from 'src/services/marketplace-api/dtos'
import { env } from 'src/configs/env'
import JsonLdScript from 'src/components/jsonLdScript'
import { removerTags } from 'src/util/formatar'
import {
  createProductStructuredData,
  ProductMetadata,
} from 'src/util/metadata/google-metadata'

type HeadProductPageProps = {
  productContent: ProductResponse
  activeVariation: Variation
  activeAd: number
}

export default function HeadProductPage({
  productContent,
  activeVariation,
  activeAd,
}: HeadProductPageProps) {
  const metaImageUrl =
    activeVariation?.imagens[0]?.url ??
    productContent?.variacoes[0]?.imagens[0]?.url ??
    ''

  const quantityAvailable =
    activeVariation.anuncios?.find((anuncio) => anuncio.id === activeAd)
      ?.estoque ?? 0

  const productMetadata: ProductMetadata = useMemo(() => {
    return {
      brand: productContent.marca_slug,
      image: metaImageUrl,
      name: productContent.titulo
        ? productContent.titulo.replace(/"/gm, "'")
        : '',
      description: removerTags(productContent.descricao),
      price: activeVariation.anuncios?.find(
        (anuncio) => anuncio.id === activeAd
      )?.formas_pagamento.boleto.por,
      sku: activeVariation.id,
      availability: quantityAvailable > 0 ? 'in stock' : 'out of stock',
      review: {
        ratingValue: productContent.avaliacoes?.totais.media,
        reviewCount: productContent.avaliacoes?.totais.quantidade_avaliacoes,
        comments: productContent.avaliacoes?.comentarios.map((comment) => ({
          author: comment.nome,
          ratingValue: comment.nota,
          datePublished: comment.data_publicacao,
          reviewBody: comment.comentario,
        })),
      },
    }
  }, [productContent, activeVariation, activeAd])

  useEffect(() => {
    if (activeVariation && activeAd) {
      const defineDadosGTM = async () => {
        gtg.productDetails(productContent, activeVariation)
        gtm.productDetails(productContent, activeVariation)
      }
      defineDadosGTM()
    }
  }, [activeVariation, activeAd, productContent])

  useEffect(() => {
    const defineDadosFacebook = async () => {
      facebookActions.viewContent(productContent)
    }
    defineDadosFacebook()
  }, [productContent])

  return (
    <>
      <Head>
        <title>
          {productContent?.seo_titulo ??
            'Gazin - Loja de telefonia, eletrodomésticos e muito mais!'}
        </title>
        <meta
          name="description"
          content={
            productContent?.seo_descricao ??
            'Compre celulares, televisores, games, tablets, móveis, eletrodomésticos e muito mais aqui na Gazinshop.com.br, tudo em até 12x s/ juros. Aproveite!'
          }
          key="description"
        />

        {/* Facebook Sharing */}
        <meta
          property="og:title"
          content={productContent?.seo_titulo ?? ''}
          key="og:title"
        />
        <meta
          property="og:description"
          content={productContent?.seo_descricao ?? ''}
          key="og:description"
        />
        <meta property="og:image" content={metaImageUrl} key="og:image" />
        <meta
          property="og:image:secure_url"
          content={metaImageUrl}
          key="og:image:secure_url"
        />
      </Head>

      <JsonLdScript
        id="google-metadata"
        key={activeVariation.id}
        content={createProductStructuredData(productMetadata)}
      />

      <Script
        id="microsoft-clarity"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            (function(c,l,a,r,i,t,y){
              c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
              t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
              y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
            })(window, document, "clarity", "script", "${env.microsoftClarity.projectId}");
          `,
        }}
      />
    </>
  )
}
